import React, {useCallback, useRef} from 'react'
import useResizeObserver from 'use-resize-observer'
import {useOnWindowResize} from '@containers/hooks'

const fadeThreshold = 48
const fadeLength = 24
const fadePart = 0.5

export const useScrollFade = (
  ref: React.RefObject<HTMLElement | undefined>
) => {
  const refreshFadeImpl = useCallback(() => {
    if (ref.current) {
      const {scrollTop, scrollHeight} = ref.current
      const {height} = ref.current.getBoundingClientRect()
      const p = (scrollHeight - (scrollTop + height)) / fadeThreshold
      const opacity = 1 - Math.max(0, Math.min(1, p))
      const length = Math.min(fadeLength, height * fadePart)

      const mask = `linear-gradient(0deg, rgba(0, 0, 0, ${opacity}) 0%, black ${length}px)`
      ref.current.style.maskImage = mask
      ref.current.style.webkitMaskImage = mask
    }
  }, [ref])

  // Run only once per frame
  const handlerRef = useRef<number | undefined>()
  const refreshFade = useCallback(() => {
    if (!handlerRef.current) {
      handlerRef.current = requestAnimationFrame(() => {
        handlerRef.current = undefined
        refreshFadeImpl()
      })
    }
  }, [handlerRef, refreshFadeImpl])

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore works fine even w/ type mismatch, perfect-scrollbar has strange typing
  useResizeObserver({ref, onResize: refreshFade})

  useOnWindowResize(refreshFade)

  return {refreshFade}
}
